import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Schedule,
  Warning,
  CheckCircle,
  Agriculture,
} from '@mui/icons-material';

/**
 * DOC Progress Indicator Component
 * 
 * Displays Days of Culture (DOC) progress with visual indicators
 * Used across: Dashboard, Policy details, Claim screen
 * 
 * @param {Object} props
 * @param {string|Date} props.stockingDate - Stocking date (ISO string or Date object)
 * @param {number} props.targetDOC - Target DOC (days) from policy or pond
 * @param {string} props.variant - 'circular' | 'linear' | 'compact' (default: 'circular')
 * @param {string} props.size - 'small' | 'medium' | 'large' (default: 'medium')
 * @param {boolean} props.showDetails - Show detailed information (default: true)
 * @param {string} props.label - Custom label (optional)
 */
const DOCProgressIndicator = ({
  stockingDate,
  targetDOC = 120,
  variant = 'circular',
  size = 'medium',
  showDetails = true,
  label = 'Days of Culture',
}) => {
  // Calculate current DOC
  const calculateCurrentDOC = () => {
    if (!stockingDate) return null;
    const today = new Date();
    const stockDate = new Date(stockingDate);
    const diffTime = Math.abs(today - stockDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    const currentDOC = calculateCurrentDOC();
    if (currentDOC === null || !targetDOC) return 0;
    const progress = Math.min((currentDOC / targetDOC) * 100, 100);
    return Math.max(0, progress);
  };

  // Calculate days remaining
  const calculateDaysRemaining = () => {
    const currentDOC = calculateCurrentDOC();
    if (currentDOC === null || !targetDOC) return null;
    const remaining = targetDOC - currentDOC;
    return remaining;
  };

  // Get status color and icon
  const getStatusConfig = () => {
    const currentDOC = calculateCurrentDOC();
    const daysRemaining = calculateDaysRemaining();
    
    if (currentDOC === null) {
      return { color: 'default', icon: <Schedule />, severity: 'info' };
    }
    
    if (currentDOC >= targetDOC) {
      return { color: 'success', icon: <CheckCircle />, severity: 'success' };
    }
    
    if (daysRemaining !== null && daysRemaining <= 7) {
      return { color: 'warning', icon: <Warning />, severity: 'warning' };
    }
    
    if (daysRemaining !== null && daysRemaining <= 0) {
      return { color: 'error', icon: <Warning />, severity: 'error' };
    }
    
    return { color: 'primary', icon: <Agriculture />, severity: 'info' };
  };

  const currentDOC = calculateCurrentDOC();
  const progress = calculateProgress();
  const daysRemaining = calculateDaysRemaining();
  const statusConfig = getStatusConfig();

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('DOCProgressIndicator props:', {
      stockingDate,
      targetDOC,
      variant,
      currentDOC,
      progress,
      daysRemaining
    });
  }

  // Size configurations
  const sizeConfig = {
    small: { circular: 60, fontSize: '0.75rem', iconSize: 16 },
    medium: { circular: 100, fontSize: '0.875rem', iconSize: 24 },
    large: { circular: 140, fontSize: '1rem', iconSize: 32 },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  // Render circular variant
  if (variant === 'circular') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minHeight: '120px' }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={progress}
            size={config.circular}
            thickness={4}
            sx={{
              color: statusConfig.color === 'success' ? 'success.main' :
                     statusConfig.color === 'warning' ? 'warning.main' :
                     statusConfig.color === 'error' ? 'error.main' : 'primary.main',
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                fontSize: config.fontSize,
                color: 'text.primary',
              }}
            >
              {currentDOC !== null ? `${currentDOC}` : '--'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: config.fontSize === '1rem' ? '0.7rem' : '0.6rem',
                color: 'text.secondary',
              }}
            >
              / {targetDOC}
            </Typography>
          </Box>
        </Box>
        {showDetails && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {label}
            </Typography>
            {daysRemaining !== null && (
              <Chip
                label={`${daysRemaining > 0 ? `${daysRemaining} days left` : 'Harvest due'}`}
                size="small"
                color={statusConfig.color}
                icon={statusConfig.icon}
                sx={{ mt: 0.5 }}
              />
            )}
            {!stockingDate && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                No stocking date available
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Render linear variant
  if (variant === 'linear') {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {currentDOC !== null ? `${currentDOC}` : '--'} / {targetDOC} days
            </Typography>
            {statusConfig.icon && (
              <Tooltip title={daysRemaining !== null ? `${daysRemaining} days remaining` : 'No stocking date'}>
                <Box sx={{ color: `${statusConfig.color}.main` }}>
                  {React.cloneElement(statusConfig.icon, { fontSize: 'small' })}
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              backgroundColor: statusConfig.color === 'success' ? 'success.main' :
                               statusConfig.color === 'warning' ? 'warning.main' :
                               statusConfig.color === 'error' ? 'error.main' : 'primary.main',
            },
          }}
        />
        {showDetails && daysRemaining !== null && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <Chip
              label={daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Harvest due'}
              size="small"
              color={statusConfig.color}
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    );
  }

  // Render compact variant
  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {statusConfig.icon && (
          <Box sx={{ color: `${statusConfig.color}.main` }}>
            {React.cloneElement(statusConfig.icon, { fontSize: 'small' })}
          </Box>
        )}
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          DOC: {currentDOC !== null ? `${currentDOC}` : '--'}/{targetDOC}
        </Typography>
        {daysRemaining !== null && daysRemaining <= 7 && (
          <Chip
            label={daysRemaining > 0 ? `${daysRemaining}d left` : 'Due'}
            size="small"
            color={statusConfig.color}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        )}
      </Box>
    );
  }

  // Render card variant (detailed view)
  if (variant === 'card') {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={progress}
                  size={120}
                  thickness={5}
                  sx={{
                    color: statusConfig.color === 'success' ? 'success.main' :
                           statusConfig.color === 'warning' ? 'warning.main' :
                           statusConfig.color === 'error' ? 'error.main' : 'primary.main',
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {currentDOC !== null ? `${currentDOC}` : '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / {targetDOC} days
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="h6" gutterBottom>
                {label}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    mb: 1,
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {progress.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              {stockingDate && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Stocked: {new Date(stockingDate).toLocaleDateString()}
                </Typography>
              )}
              {daysRemaining !== null && (
                <Chip
                  label={daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Harvest due'}
                  color={statusConfig.color}
                  icon={statusConfig.icon}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default DOCProgressIndicator;

